import * as React from 'react';
import { cn } from '../../../utils';
import { Copy, Kanban, SixDots, Tabular, ThreeDots } from '../../Icon/icon';
import { Arrow, Edit, Star, Trash } from 'rcd-cpp-ui/ui/icon';
import { Dropdown } from '../../Dropdown/dropdown';
import { useDrag } from 'react-dnd';
import { ViewType } from '../../../enums/ViewType';
import { useSelector } from '../../../utils/useSelector';
import { Role } from '../../../enums/Role';
import { View } from '../../../types';
import { NavViewOptionName } from '../../../enums/NavViewOptionName';
import { useActions as useNavActions } from '../navActions';
import { useViewDrop } from './navView';
import URLManager from '../../../utils/urlManager';
import { ContextMenuLink } from '../../ContextMenuLink/contextMenuLink';

interface ViewItemProps {
  disabled: boolean;
  handleSelectOption: (o, item?, folderRefId?: string) => void;
  handleSelectView: (v: View.Setting) => void;
  selectedItemId: string;
  v: View.Setting;
  inFavoritedFolder?: boolean;
  index?: number;
  parentRefId?: string;
  onMoveView?: (viewId: string, toFolderId: string, toIndex: number) => void;
}

export const ViewItem = ({
  disabled,
  handleSelectOption,
  handleSelectView,
  selectedItemId,
  v,
  inFavoritedFolder,
  index,
  parentRefId,
  onMoveView
}: ViewItemProps) => {
  const isEFSMgr = isEFSManager();
  let userSettings = useSelector(s => s.planning?.userSettings);
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);

  const { onDrop } = useNavActions();
  const [showMenuForId, setShowMenuForId] = React.useState<string>(null);
  let itemOptions: ItemOpt[] = [];

  if (!disabled) {
    itemOptions = v.isMyItemsView
      ? [ItemOptions[0]]
      : !inFavoritedFolder
        ? [ItemOptions[0], ItemOptions[1], ItemOptions[2], ItemOptions[3]]
        : [ItemOptions[4]];
    if (v.isBacktaggingView) {
      itemOptions = itemOptions.filter(
        o => NavViewOptionName.Favorite != o.name
      );
    }
    if (v.props.type == ViewType.Report) {
      itemOptions = ItemOptions.filter(
        o => o.name == NavViewOptionName.Favorite
      );
    }

    if (
      userSettings[v?.props?.refId]?.props?.isFavorited &&
      !inFavoritedFolder
    ) {
      itemOptions = [
        ItemOptions[0],
        ItemOptions[1],
        ItemOptions[2],
        ItemOptions[4]
      ];
    }
  } else {
    itemOptions = !inFavoritedFolder ? [ItemOptions[3]] : [ItemOptions[4]];

    if (
      userSettings[v?.props?.refId]?.props?.isFavorited &&
      !inFavoritedFolder
    ) {
      itemOptions = [ItemOptions[4]];
    }
  }
  if (!!v.parent) {
    itemOptions = itemOptions.filter(
      o => ![NavViewOptionName.Copy, NavViewOptionName.Delete].includes(o.name)
    );
  }

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'View',
      item: () => {
        return { refId: v.props.refId, parentRefId, index };
      },
      collect: monitor => ({
        isDragging: monitor.isDragging()
      }),
      canDrag:
        !disabled &&
        !v.isMyItemsView &&
        !inFavoritedFolder &&
        !v.isBacktaggingView
    }),
    [v, parentRefId, index, disabled, inFavoritedFolder]
  );
  const [{ isOver, isOverCurrent }, drop] = useViewDrop({
    isSystemFolder: false,
    greedy: false,
    onDropHandler: () => {
      onDrop();
    },
    onHover: item => {
      if (item.refId != v.props.refId && onMoveView) {
        onMoveView(item.refId, parentRefId, index);
      }
    }
  });

  const canMove =
    !disabled && !v.isMyItemsView && !inFavoritedFolder && !v.isBacktaggingView;
  const href = URLManager.generateUrl(selectedWorkspace, { v: v.props.refId });

  if (v.props?.isEFSBuilder) {
    if (isEFSMgr) {
      itemOptions = ItemOptions.filter(o => o.name == NavViewOptionName.Edit);
    } else {
      itemOptions = itemOptions.filter(o => o.name == NavViewOptionName.Edit);
    }
  }

  return (
    <div
      className="nav-group"
      ref={node => {
        if (canMove && typeof drop == 'function') drag(drop(node));
      }}
      style={{
        outline: isDragging
          ? '.25px solid #0052d6'
          : isOverCurrent
            ? '.25px solid #0052d6'
            : '.25px solid transparent'
      }}
    >
      <div
        key={v.props.refId}
        className={cn(
          {
            'selected-ngi': v.id == selectedItemId,
            'show-ngi-menu': v.id == showMenuForId,
            'has-ngi-menu': !!itemOptions?.length
          },
          'nav-group-item'
        )}
      >
        <ContextMenuLink href={href} onNavigate={() => handleSelectView(v)}>
          <span>
            {canMove && <SixDots />}
            {v.props.type == ViewType.Kanban ? <Kanban /> : <Tabular />}
          </span>
          <span
            className="view-name view-list-item"
            title={v.props.name}
            data-id={v.props.refId}
          >
            {v.props.name}
          </span>
          <span>
            {v.id == selectedItemId && <Arrow direction="right" type="solid" />}
          </span>
        </ContextMenuLink>

        {!!itemOptions?.length && (
          <div className="nav-group-item-menu">
            <Dropdown
              position="right"
              options={itemOptions}
              renderOption={o => {
                const I = o.Icon;
                return (
                  <div>
                    <span>{I}</span>
                    <span className="dd-text" data-txt={o.name}>
                      {o.name}
                    </span>
                  </div>
                );
              }}
              onSelect={o => handleSelectOption(o, v)}
              onStateChanged={e => {
                setShowMenuForId(e ? v.id : null);
              }}
            >
              <ThreeDots />
            </Dropdown>
          </div>
        )}
      </div>
    </div>
  );
};

type ItemOpt = { name: NavViewOptionName; Icon: any };

const ItemOptions: ItemOpt[] = [
  { name: NavViewOptionName.Edit, Icon: <Edit /> },
  { name: NavViewOptionName.Copy, Icon: <Copy /> },
  { name: NavViewOptionName.Delete, Icon: <Trash /> },
  {
    name: NavViewOptionName.Favorite,
    Icon: <Star stroke="rgb(0, 82, 214)" width={18} height={18} />
  },
  {
    name: NavViewOptionName.Unfavorite,
    Icon: (
      <Star
        fill="rgb(0, 82, 214)"
        stroke="rgb(0, 82, 214)"
        width={18}
        height={18}
      />
    )
  }
];

const isEFSManager = () => {
  return useSelector(s => {
    if (s.accessControl.permissions && s.planning.selectedWorkspace) {
      const p =
        s.accessControl.permissions[s.planning.selectedWorkspace.props.refId];
      return p?.roles?.includes(Role.efsManager);
    }
  });
};
